import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button, message, Space, Table, Typography } from 'antd';
import { Key } from 'antd/lib/table/interface';
import { useClient } from 'src/contexts/Client/Client';
import * as rolesActions from 'src/actions/rolesActions';
import { filterMembersApiResponse } from '../utils';
import { RootState } from 'src/reducers/store';

import { ApiCustomerType } from 'src/features/Customers/types';
import { RoleType } from 'src/types';

import { GroupMemberFiltred, MembersGroupReponseApiType } from '../types';

type AddAdministratorsProps = {
  onSuccess: () => void;
  getRoles: () => void;
  groupId: string;
  customerDetail: ApiCustomerType;
  roles: Array<RoleType>;
};
const { Text } = Typography;
const MEMBERS_PER_PAGE = 10;

const AddAdministrators: React.FC<AddAdministratorsProps> = ({
  onSuccess,
  getRoles,
  groupId,
  customerDetail,
  roles,
}) => {
  const [administrators, setAdministrators] = useState<Array<string>>([]);
  const [members, setMembers] = useState<Array<GroupMemberFiltred>>([]);
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState<number>(1);
  const [total, setTotal] = useState<number>(1);

  const { get, postPromise, patchPromise } = useClient();

  useEffect(() => {
    setLoading(true);
    updateMembersList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [get, current]);

  const updateMembersList = () => {
    customerDetail &&
      get(
        `/customers/${customerDetail.id}/members?per_page=${MEMBERS_PER_PAGE}&page=${current}`,
        (response: MembersGroupReponseApiType) => {
          response.metadata && setTotal(response.metadata.total_count);
          setMembers(filterMembersApiResponse(response));
          setLoading(false);
        }
      );
  };

  const onSubmit = () => {
    const adminRole = Object.values(roles)?.find(
      (role: RoleType) => role.name === 'administrator' && role.type === 'group'
    )?.id;

    const AddAdministratorsPromises = administrators.map((id: string) => {
      const body = {
        roles: [
          {
            id: adminRole,
          },
        ],
        source: {
          id: id,
          model_type: 'user',
        },
        target: {
          id: groupId,
          model_type: 'group',
        },
      };

      return postPromise(`/groups/members`, body).then(created =>
        // Le rôle admin emporte aussi le grant de publication par adhésion.
        patchPromise(`/groups/${groupId}/members/${id}/publish`, {
          can_publish: true,
        }).catch(() => created)
      );
    });

    Promise.all(AddAdministratorsPromises)
      .then(() => {
        message.success(`Les administrateurs ont été ajouté avec succès`);
        setLoading(true);
        onSuccess();
      })
      .catch(() =>
        message.error(
          `Une erreur est survenue lors de l'ajout des administrateurs`
        )
      );
  };

  const columns = [
    {
      title: 'Nom',
      dataIndex: 'memberInfo',
      render: (member: { lastname: string }) => <Text>{member.lastname}</Text>,
      width: '25%',
    },
    {
      title: 'Prénom',
      dataIndex: 'memberInfo',
      render: (member: { firstname: string }) => (
        <Text>{member.firstname}</Text>
      ),
      width: '25%',
    },
    {
      title: 'Adresse',
      dataIndex: 'memberInfo',
      render: (member: { address: string }) => <Text>{member.address}</Text>,
      width: '40%',
    },
  ];

  const rowSelection = {
    onChange: (selectedRowKeys: Key[], selectedRows: any[]) => {
      setAdministrators(selectedRows.map((row: { id: string }) => row.id));
    },
  };

  useEffect(() => {
    if (!roles || !roles.length) return getRoles();
  }, [roles, getRoles]);

  return (
    <Space direction="vertical" size="middle">
      <Table
        key={groupId}
        columns={columns}
        loading={loading}
        dataSource={members}
        rowSelection={rowSelection}
        pagination={{
          current: current,
          total: total,
          pageSize: 10,
          showSizeChanger: false,
          position: ['bottomCenter'],
        }}
        onChange={(pagination: any) => setCurrent(pagination.current)}
      ></Table>
      <Button
        type="primary"
        style={{ width: '100%' }}
        size="middle"
        htmlType="submit"
        onClick={onSubmit}
      >
        Ajouter
      </Button>
    </Space>
  );
};

const mapStateToProps = ({
  customers: { customerDetail },
  roles: { items },
}: RootState) => {
  return {
    customerDetail,
    roles: items,
  };
};

export default connect(mapStateToProps, {
  ...rolesActions,
})(AddAdministrators);
